/**
 * @file Chooses between the two steps of a multi-part Signify fetch. Consumer:
 * `SignifyFetchJobModal`. This is the only component subscribed to `uiState.signifyFetchJob`, and it
 * reads one leaf of it — this table's own key — so a job starting under another table re-renders
 * nothing here.
 * A job that no longer exists routes to the entry step rather than blocking it: absence is proof
 * there is nothing to insert from, so Start stays live and its write replaces the stale key.
 * `signifyFetchJobNoLongerExists` decides that from `data` and `error` together, and the menu label
 * asks the same question of the same predicate, so the two can never contradict each other. Only a
 * `permission-denied` counts as gone — any other listener error may be a transient failure on a job
 * that is still running, and offering Start there would mint a second job over a live one.
 * `loading` is checked first so a store that has not answered yet shows the skeleton and never
 * flashes the notice.
 * The steps replace each other rather than coexist: once a job runs, the parts subcollection is the
 * truth and the staging list would show the same rows a second time from a second source.
 */

import { FC, useContext } from 'react';
import { Skeleton, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import FlexCol from '@/components/atoms/FlexCol';
import { UserContext } from '@/providers/UserContext';
import { uiState } from '@/store/uiState';
import SignifyFetchJobEntry from './SignifyFetchJobEntry';
import SignifyFetchJobProgress from './SignifyFetchJobProgress';
import { createSignifyFetchJobStores, signifyFetchJobNoLongerExists } from '../use/services';
import { SignifyFetchJobModalProps } from '../index.types';

const { Text } = Typography;

type SignifyFetchJobModalContentProps = Omit<SignifyFetchJobModalProps, 'open' | 'onCancel'>;

type SignifyFetchJobActiveContentProps = SignifyFetchJobModalContentProps & {
  jobId: string;
  userId: string;
};

const MISSING_JOB_NOTICE = 'The fetch this table was following no longer exists.';

/**
 * Split out because the job store may only be read once a job id exists; the router above decides
 * that, and React forbids the hooks below running conditionally.
 */
const SignifyFetchJobActiveContent: FC<SignifyFetchJobActiveContentProps> = ({
  jobId,
  userId,
  ...props
}) => {
  const { job } = createSignifyFetchJobStores(jobId, userId);
  const jobSnap = useSnapshot(job.state);

  if (jobSnap.loading) return <Skeleton active />;

  if (signifyFetchJobNoLongerExists(jobSnap)) {
    return (
      <FlexCol gap={10}>
        <Text type="secondary">{MISSING_JOB_NOTICE}</Text>
        <SignifyFetchJobEntry
          targetKey={props.targetKey}
          hasQuantityColumn={props.hasQuantityColumn}
          configDisabledReason={props.configDisabledReason}
        />
      </FlexCol>
    );
  }

  return (
    <SignifyFetchJobProgress
      jobId={jobId}
      userId={userId}
      targetKey={props.targetKey}
      disabled={props.disabled}
      onSuccess={props.onSuccess}
      onManualInsert={props.onManualInsert}
      onFindInsertedRows={props.onFindInsertedRows}
    />
  );
};

const SignifyFetchJobModalContent: FC<SignifyFetchJobModalContentProps> = (props) => {
  const activeJobId = useSnapshot(uiState.signifyFetchJob.activeJobIdByTable)[props.targetKey];
  const { user } = useContext(UserContext);
  const userId = user?.uid;

  if (activeJobId && userId) {
    return <SignifyFetchJobActiveContent {...props} jobId={activeJobId} userId={userId} />;
  }

  return (
    <SignifyFetchJobEntry
      targetKey={props.targetKey}
      hasQuantityColumn={props.hasQuantityColumn}
      configDisabledReason={props.configDisabledReason}
    />
  );
};

export default SignifyFetchJobModalContent;
